/**
 * CODED brand pill — the canonical mark, identical typography and animation
 * to genesis-sync's Sidebar.jsx + Footer.jsx. Used on every CODED-built app.
 *
 *   "CODED."   → Montserrat Bold
 *   ─────────  → 1px hairline divider
 *   framework  → Cormorant Garamond Italic
 *   plus a 6s subtle white-radiance sweep across the dark pill.
 */
type Size = 'xs' | 'sm' | 'md';

interface Props {
  size?: Size;
  className?: string;
}

const SIZES: Record<Size, { wrap: string; main: string; qual: string; gap: string; divider: string }> = {
  xs: { wrap: 'px-1.5 py-0.5', main: 'text-[10px]', qual: 'text-[9px]',  gap: 'gap-1',   divider: 'h-2.5' },
  sm: { wrap: 'px-2 py-1',     main: 'text-[12px]', qual: 'text-[11px]', gap: 'gap-1.5', divider: 'h-3'   },
  md: { wrap: 'px-3 py-1.5',   main: 'text-[14px]', qual: 'text-[12px]', gap: 'gap-2',   divider: 'h-3.5' },
};

export default function CodedPill({ size = 'sm', className = '' }: Props) {
  const s = SIZES[size];
  return (
    <a
      href="https://coded.co.za"
      target="_blank"
      rel="noopener noreferrer"
      title="Built on the CODED framework — coded.co.za"
      className={[
        'coded-pill group inline-flex items-center justify-center rounded-md',
        'border border-white/[0.06] hover:border-white/20 transition-colors',
        s.wrap, s.gap, className,
      ].join(' ')}
    >
      <span
        className={[
          'font-montserrat font-bold tracking-tight leading-none text-[#FFFFFF]',
          s.main,
        ].join(' ')}
      >
        CODED.
      </span>
      <span
        className={[s.divider, 'w-px bg-white/15 group-hover:bg-white/30 transition-colors'].join(' ')}
        aria-hidden="true"
      />
      <span
        className={[
          'font-cormorant italic font-bold leading-none tracking-[0.01em] text-[#FFFFFF]',
          s.qual,
        ].join(' ')}
      >
        framework
      </span>
    </a>
  );
}
